import { useCallback, useState, useEffect } from 'react';
import { KycDocument, UserProfile } from '../types/profile';
import { useSnackbar } from 'notistack';
import { useGetCustomerProfile, useUpdateCustomerProfile } from '../api/auth/use-customer-auth.tsx';

export const useProfile = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { data: userData, isLoading, refetch } = useGetCustomerProfile();
    const updateProfileMutation = useUpdateCustomerProfile();

    const [profile, setProfile] = useState<UserProfile>({
        id: '',
        email: '',
        username: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        country: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        twoFactorEnabled: false,
        kycStatus: 'none',
        createdAt: '',
        updatedAt: '',
    });

    const [kycDocuments, setKycDocuments] = useState<KycDocument[]>([]);

    // Update profile state when API data changes
    useEffect(() => {
        if (userData?.data) {
            const user = userData.data;
            const nameParts = user.name.split(' ');

            setProfile({
                id: user.id.toString(),
                email: user.email,
                username: user.name,
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                phoneNumber: user.phone || '',
                country: user.address?.country || '',
                address: user.address?.address || '',
                city: user.address?.city || '',
                state: user.address?.state || '',
                postalCode: user.address?.postalCode || '',
                twoFactorEnabled: false,
                kycStatus: user.kyc_status || 'none',
                createdAt: user.created_at || '',
                updatedAt: user.updated_at || '',
            });
        }
    }, [userData]);

    const updateProfile = useCallback(
        async (updates: Partial<UserProfile>) => {
            try {
                await updateProfileMutation.mutateAsync({
                    firstName: updates.firstName || profile.firstName,
                    lastName: updates.lastName || profile.lastName,
                    email: updates.email || profile.email,
                    phoneNumber: updates.phoneNumber,
                    country: updates.country,
                    address: updates.address,
                    city: updates.city,
                    state: updates.state,
                    postalCode: updates.postalCode,
                });

                enqueueSnackbar('Profile updated successfully', { variant: 'success' });

                // Refetch profile data to get the latest information
                await refetch();
            } catch (error: any) {
                const errorMessage = error.response?.data?.message || 'Failed to update profile';
                enqueueSnackbar(errorMessage, { variant: 'error' });
                throw error;
            }
        },
        [updateProfileMutation, profile, enqueueSnackbar, refetch]
    );

    const uploadKycDocument = useCallback(
        (document: Omit<KycDocument, 'id' | 'status' | 'uploadedAt'>) => {
            const newDocument: KycDocument = {
                ...document,
                id: Math.random().toString(36).substr(2, 9),
                status: 'pending',
                uploadedAt: new Date().toISOString(),
            };
            setKycDocuments(prev => [...prev, newDocument]);
            return newDocument;
        },
        []
    );

    return {
        profile,
        isLoading: isLoading || updateProfileMutation.isPending,
        kycDocuments,
        updateProfile,
        uploadKycDocument,
    };
};
